import { useEffect, useMemo, useRef } from 'react'
import { ShaderMaterial, Color, Mesh, Group, Vector2, DoubleSide, AdditiveBlending, NormalBlending } from 'three'
import { useFrame } from '@react-three/fiber'

// Base name of the beam mesh(es) authored in the glTF (material: x_volumetric).
// GLTFLoader strips the dot from suffixed duplicates (voluLightshaft.001 ->
// voluLightshaft001), so match on the sanitised base prefix to catch them all.
const BEAM_MESH = 'voluLightshaft'
const isBeamMesh = (name: string) => name.startsWith(BEAM_MESH)

// Beam look per camera. `fallback` is used for any camera without an entry;
// per-camera entries are partial overrides merged on top of it.
const DEFAULT_SETTINGS = {
  color: '#ffcc99',
  baseOpacity: 1.12,
  additive: true,
  radialFade: true,
  center: { x: 0, y: 0.5 },
  rotation: -20,
  right: 0.84,
  left: 0.2,
  up: 0.4,
  down: 0.51,
  falloffPower: 2,
  noise: true,
  noiseScale: { x: 0, y: 20 },
  noiseRotation: 0,
  noiseSpeed: 0.38,
  noiseFloor: 0,
  noiseAmount: 1,
  dust: true,
  dustIntensity: 1.2,
  dustScale: 65,
  dustSize: 0.06,
  dustSpeed: 0.47,
}

// Per-camera overrides (merged onto `fallback`), keyed by active camera index.
// Cameras without an entry (e.g. 4) use the fallback.
const SETTINGS_BY_CAM: Record<string | number, Partial<typeof DEFAULT_SETTINGS>> = {
  fallback: DEFAULT_SETTINGS,
  0: { baseOpacity: 0.49, right: 0.94, down: 0.47, noiseSpeed: 0.27, dustIntensity: 2, dustSize: 0.05 },
  1: { baseOpacity: 0.46, center: { x: 0.17, y: 0.5 }, right: 0.89, up: 0.28, down: 0.35 },
  2: { baseOpacity: 0.47, center: { x: 0.2, y: 0.47 }, right: 0.65, up: 0.29, down: 0.22 },
  3: { baseOpacity: 0.56, center: { x: 0.1, y: 0.45 }, left: 0.05 },
  5: { baseOpacity: 0.57, center: { x: 0.13, y: 0.53 }, right: 0.62, left: 0.05, down: 0.31 },
  7: { right: 1.19 },
  9: { center: { x: 0.17, y: 0.48 }, right: 0.73, left: 0.15, up: 0.3, down: 0.28 },
  // 6 and 8 match the fallback.
}

const vertexShader = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const fragmentShader = /* glsl */ `
  uniform float uTime;
  uniform vec3 uColor;
  uniform float uBaseOpacity;
  uniform vec2 uCenter;
  uniform float uRotation;
  uniform float uLeft;
  uniform float uRight;
  uniform float uUp;
  uniform float uDown;
  uniform float uFalloffPower;
  uniform vec2 uNoiseScale;
  uniform float uNoiseSpeed;
  uniform float uNoiseFloor;
  uniform float uNoiseAmount;
  uniform float uNoiseRotation;
  uniform float uDustOn;
  uniform float uDustIntensity;
  uniform float uDustScale;
  uniform float uDustSize;
  uniform float uDustSpeed;
  uniform float uRadialOn;
  uniform float uNoiseOn;

  varying vec2 vUv;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
               mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }

  float fbm(vec2 p) {
    return valueNoise(p) * 0.65 + valueNoise(p * 2.03 + 7.3) * 0.35;
  }

  vec2 hash22(vec2 p) {
    vec3 a = fract(vec3(p.xyx) * vec3(123.34, 234.34, 345.65));
    a += dot(a, a + 34.45);
    return fract(vec2(a.x * a.y, a.y * a.z));
  }

  // Drifting, twinkling dust motes: one mote per grid cell (3x3 neighbourhood so
  // they don't clip at cell borders), slowly hovering and pulsing in brightness.
  float dust(vec2 uv, float t) {
    vec2 p = uv * uDustScale;
    vec2 id = floor(p);
    vec2 gv = fract(p);
    float acc = 0.0;
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 cell = vec2(float(i), float(j));
        vec2 rnd = hash22(id + cell);
        vec2 drift = 0.18 * vec2(sin(t * (0.6 + rnd.x) + rnd.y * 6.2831),
                                 cos(t * (0.5 + rnd.y) + rnd.x * 6.2831));
        vec2 pos = cell + 0.5 + (rnd - 0.5) * 0.6 + drift;
        float d = length(gv - pos);
        float twinkle = 0.5 + 0.5 * sin(t * 3.0 + (rnd.x + rnd.y) * 18.0);
        acc += smoothstep(uDustSize, 0.0, d) * twinkle;
      }
    }
    return acc;
  }

  void main() {
    // Elliptical body: fade from uCenter out to transparent, with independent
    // left/right/up/down extents and an optional rotation of the whole shape.
    // The plane's UV is 90° to its visual orientation, so rotate the falloff
    // frame to match (centre.x -> horizontal, centre.y -> vertical on screen).
    vec2 fuv = vec2(1.0 - vUv.y, vUv.x);
    vec2 p = fuv - uCenter;
    float cs = cos(uRotation);
    float sn = sin(uRotation);
    vec2 rp = vec2(cs * p.x - sn * p.y, sn * p.x + cs * p.y);
    float rx = rp.x >= 0.0 ? uRight : uLeft;
    float ry = rp.y >= 0.0 ? uUp : uDown;
    float dist = length(vec2(rp.x / rx, rp.y / ry));
    float radial = pow(clamp(1.0 - dist, 0.0, 1.0), uFalloffPower);
    radial = mix(1.0, radial, uRadialOn);

    // Animated procedural noise, in the same oriented frame as the falloff.
    // Inherits the falloff rotation; uNoiseRotation is an extra offset on top.
    float na = uRotation + uNoiseRotation;
    float ncs = cos(na);
    float nsn = sin(na);
    vec2 nuv = vec2(ncs * fuv.x - nsn * fuv.y, nsn * fuv.x + ncs * fuv.y);
    float n = fbm(nuv * uNoiseScale + vec2(uTime * uNoiseSpeed, 0.0));
    float noise = mix(1.0, mix(uNoiseFloor, 1.0, n), uNoiseOn * uNoiseAmount);

    float alpha = uBaseOpacity * radial * noise;

    // Dust shimmer: bright specks within the beam body, modulated by the noise
    // field. Skipped entirely when off or outside the beam (the 3x3 mote loop is
    // the heaviest part of the shader, so the branch keeps it off the hot path).
    if (uDustOn > 0.5 && radial > 0.0) {
      alpha += dust(fuv, uTime * uDustSpeed) * radial * noise * uDustIntensity;
    }

    gl_FragColor = vec4(uColor, alpha);
  }
`

interface VolumetricBeamProps {
  scene: Group
  activeCam: number
}

export default function VolumetricBeam({ scene, activeCam }: VolumetricBeamProps) {
  const matRef = useRef<ShaderMaterial | null>(null)
  const secondMatRef = useRef<ShaderMaterial | null>(null)

  // Fixed per-camera look (no runtime controls): fallback merged with the active
  // camera's overrides.
  const ctrl = useMemo(
    () => ({ ...DEFAULT_SETTINGS, ...(SETTINGS_BY_CAM[activeCam] ?? {}) }),
    [activeCam],
  )

  useEffect(() => {
    const meshes: Mesh[] = []
    scene.traverse((obj) => {
      if ((obj as Mesh).isMesh && isBeamMesh(obj.name)) meshes.push(obj as Mesh)
    })
    if (meshes.length === 0) {
      console.warn(`VolumetricBeam: no mesh matching "${BEAM_MESH}" found in scene`)
      return
    }

    const material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(ctrl.color) },
        uBaseOpacity: { value: ctrl.baseOpacity },
        uCenter: { value: new Vector2(ctrl.center.x, ctrl.center.y) },
        uRotation: { value: (ctrl.rotation * Math.PI) / 180 },
        uLeft: { value: ctrl.left },
        uRight: { value: ctrl.right },
        uUp: { value: ctrl.up },
        uDown: { value: ctrl.down },
        uFalloffPower: { value: ctrl.falloffPower },
        uNoiseScale: { value: new Vector2(ctrl.noiseScale.x, ctrl.noiseScale.y) },
        uNoiseSpeed: { value: ctrl.noiseSpeed },
        uNoiseFloor: { value: ctrl.noiseFloor },
        uNoiseAmount: { value: ctrl.noiseAmount },
        uNoiseRotation: { value: (ctrl.noiseRotation * Math.PI) / 180 },
        uDustOn: { value: 1 },
        uDustIntensity: { value: ctrl.dustIntensity },
        uDustScale: { value: ctrl.dustScale },
        uDustSize: { value: ctrl.dustSize },
        uDustSpeed: { value: ctrl.dustSpeed },
        uRadialOn: { value: 1 },
        uNoiseOn: { value: 0 },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      side: DoubleSide,
      blending: AdditiveBlending,
      toneMapped: false,
    })

    // The second beam can be dimmed independently (e.g. hidden on cam1/cam11), so
    // give it its own material instance with cloned uniforms.
    const secondMaterial = meshes.length > 1 ? material.clone() : null

    const prevMaterials = meshes.map((mesh, i) => {
      const prev = mesh.material
      mesh.material = i === 1 && secondMaterial ? secondMaterial : material
      mesh.castShadow = false
      mesh.receiveShadow = false
      return prev
    })
    matRef.current = material
    secondMatRef.current = secondMaterial

    return () => {
      meshes.forEach((mesh, i) => { mesh.material = prevMaterials[i] })
      material.dispose()
      secondMaterial?.dispose()
      matRef.current = null
      secondMatRef.current = null
    }
    // Material is created once; per-camera values are pushed live in useFrame below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene])

  // Blend mode requires a material recompile, so handle it separately.
  useEffect(() => {
    const blending = ctrl.additive ? AdditiveBlending : NormalBlending
    for (const material of [matRef.current, secondMatRef.current]) {
      if (!material) continue
      material.blending = blending
      material.needsUpdate = true
    }
  }, [ctrl.additive])

  // The look is fixed per camera, so push the uniforms only when the camera (or
  // material) changes — not every frame. The second beam (body + dust) is hidden
  // on cam1 and cam11 (indices 0 and 10).
  useEffect(() => {
    const apply = (material: ShaderMaterial | null, hidden: boolean) => {
      if (!material) return
      const u = material.uniforms
      u.uColor.value.set(ctrl.color)
      u.uBaseOpacity.value = hidden ? 0 : ctrl.baseOpacity
      u.uCenter.value.set(ctrl.center.x, ctrl.center.y)
      u.uRotation.value = (ctrl.rotation * Math.PI) / 180
      u.uLeft.value = ctrl.left
      u.uRight.value = ctrl.right
      u.uUp.value = ctrl.up
      u.uDown.value = ctrl.down
      u.uFalloffPower.value = ctrl.falloffPower
      u.uNoiseScale.value.set(ctrl.noiseScale.x, ctrl.noiseScale.y)
      u.uNoiseSpeed.value = ctrl.noiseSpeed
      u.uNoiseFloor.value = ctrl.noiseFloor
      u.uNoiseAmount.value = ctrl.noiseAmount
      u.uNoiseRotation.value = (ctrl.noiseRotation * Math.PI) / 180
      // Dust is added on top of base opacity, so hide it too (else specks remain).
      u.uDustOn.value = !hidden && ctrl.dust ? 1 : 0
      u.uDustIntensity.value = ctrl.dustIntensity
      u.uDustScale.value = ctrl.dustScale
      u.uDustSize.value = ctrl.dustSize
      u.uDustSpeed.value = ctrl.dustSpeed
      u.uRadialOn.value = ctrl.radialFade ? 1 : 0
      u.uNoiseOn.value = ctrl.noise ? 1 : 0
    }
    apply(matRef.current, false)
    apply(secondMatRef.current, activeCam === 0 || activeCam === 10)
  }, [ctrl, activeCam])

  // Only the animation clock changes per frame.
  useFrame((state) => {
    const t = state.clock.elapsedTime
    if (matRef.current) matRef.current.uniforms.uTime.value = t
    if (secondMatRef.current) secondMatRef.current.uniforms.uTime.value = t
  })

  return null
}
